/**
 * Data Input validation utilities
 * Provides validation functions for data input form fields according to business rules
 */

/**
 * Extracts numeric value from mixed input (handles paste with non-numeric content)
 * @param {string|number} value - The input value
 * @returns {string|null} - Extracted numeric value or null if no valid number found
 */
function extractNumericValue(value) {
  if (value === null || value === undefined) {
    return null
  }
  
  const stringValue = String(value)
  const numericMatch = stringValue.match(/\d+/)
  return numericMatch ? numericMatch[0] : null
}

/**
 * Extracts IP-valid characters from mixed input (handles paste with invalid content)
 * @param {string} value - The input value
 * @returns {string} - Extracted IP-valid characters (numbers and dots only)
 */
function extractIPValidChars(value) {
  if (value === null || value === undefined) {
    return ''
  }
  
  const stringValue = String(value)
  return stringValue.replace(/[^0-9.]/g, '')
}

/**
 * Extracts name-valid characters from mixed input (handles paste with invalid content)
 * @param {string} value - The input value
 * @returns {string} - Extracted name-valid characters (alphanumeric, hyphen, underscore, dot only)
 */
function extractNameValidChars(value) {
  if (value === null || value === undefined) {
    return ''
  }
  
  const stringValue = String(value)
  return stringValue.replace(/[^a-zA-Z0-9._-]/g, '')
}

/**
 * Validates data source name according to business rules
 * @param {string} name - The name to validate
 * @param {Function} $t - Translation function for error messages
 * @param {Array} existingDataSources - Array of existing data source names
 * @param {boolean} isEdit - Whether this is an edit operation
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateDataSourceName(name, $t, existingDataSources = [], isEdit = false) {
  if (!name || name.trim() === '') {
    return {
      isValid: false,
      error: $t('dataInputs.validation.nameRequired')
    }
  }

  const trimmedName = name.trim()

  // Length validation
  if (trimmedName.length < 3) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.nameTooShort')
    }
  }

  if (trimmedName.length > 30) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.nameTooLong')
    }
  }

  // Character validation - only alphanumeric, hyphen, underscore, dot allowed
  const nameRegex = /^[a-zA-Z0-9._-]+$/
  if (!nameRegex.test(trimmedName)) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.nameInvalidChars')
    }
  }

  // Uniqueness validation (case-insensitive)
  if (!isEdit && existingDataSources.some(existing => 
    existing.source_rename && existing.source_rename.toLowerCase() === trimmedName.toLowerCase())) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.nameExists')
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Validates data source description according to business rules
 * @param {string} description - The description to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean, error message, and character count
 */
export function validateDataSourceDescription(description, $t) {
  const maxLength = 4096
  const currentLength = description ? description.length : 0

  if (currentLength > maxLength) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.descriptionTooLong', { max: maxLength }),
      characterCount: currentLength,
      maxLength
    }
  }

  return {
    isValid: true,
    error: '',
    characterCount: currentLength,
    maxLength
  }
}

/**
 * Validates port number according to business rules
 * @param {string|number} port - The port to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean, error message, and corrected value
 */
export function validatePort(port, $t) {
  // Extract numeric value from input (handle paste with non-numeric content)
  const numericValue = extractNumericValue(port)
  
  if (numericValue === null || numericValue === '') {
    return {
      isValid: false,
      error: $t('dataInputs.validation.portRequired'),
      correctedValue: ''
    }
  }

  const parsedValue = parseInt(numericValue, 10)
  
  if (isNaN(parsedValue)) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.portInvalid'),
      correctedValue: ''
    }
  }

  // Range validation
  if (parsedValue < 0 || parsedValue > 65535) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.portOutOfRange'),
      correctedValue: ''
    }
  }

  // Reserved ports validation
  if (parsedValue === 80 || parsedValue === 443) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.portReserved'),
      correctedValue: ''
    }
  }

  return {
    isValid: true,
    error: '',
    correctedValue: String(parsedValue)
  }
}

/**
 * Validates individual IP address format
 * @param {string} ip - The IP address to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateIPAddress(ip, $t) {
  if (!ip || ip.trim() === '') {
    return {
      isValid: false,
      error: $t('dataInputs.validation.ipRequired')
    }
  }

  const trimmedIP = ip.trim()

  // Length validation
  if (trimmedIP.length > 16) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.ipTooLong')
    }
  }

  // Basic IP format validation (simple check for IPv4)
  const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/
  if (!ipRegex.test(trimmedIP)) {
    return {
      isValid: false,
      error: $t('dataInputs.validation.ipInvalidFormat')
    }
  }

  // Validate each octet
  const octets = trimmedIP.split('.')
  for (const octet of octets) {
    const num = parseInt(octet, 10)
    if (num < 0 || num > 255) {
      return {
        isValid: false,
        error: $t('dataInputs.validation.ipInvalidRange')
      }
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Validates allowed IP addresses list
 * @param {Array} ipList - Array of IP addresses to validate
 * @param {Function} $t - Translation function for error messages
 * @returns {Object} - Validation result with isValid boolean and error message
 */
export function validateAllowedIPs(ipList, $t) {
  // Empty list is allowed (means allow all IPs)
  if (!ipList || ipList.length === 0) {
    return {
      isValid: true,
      error: ''
    }
  }

  // Validate each IP in the list
  for (const ip of ipList) {
    const ipValidation = validateIPAddress(ip, $t)
    if (!ipValidation.isValid) {
      return ipValidation
    }
  }

  return {
    isValid: true,
    error: ''
  }
}

/**
 * Applies character filtering to form inputs
 * @param {string} value - The input value
 * @param {string} type - The type of filtering ('name', 'port', 'ip')
 * @returns {string} - Filtered value
 */
export function applyInputFilter(value, type) {
  switch (type) {
    case 'name':
      return extractNameValidChars(value)
    case 'port':
      return extractNumericValue(value) || ''
    case 'ip':
      return extractIPValidChars(value)
    default:
      return value
  }
}

/**
 * Validates all data input form fields
 * @param {Object} formData - The form data object
 * @param {Function} $t - Translation function for error messages
 * @param {Array} existingDataSources - Array of existing data source names
 * @param {boolean} isEdit - Whether this is an edit operation
 * @returns {Object} - Validation result with overall validity and field-specific errors
 */
export function validateDataInputForm(formData, $t, existingDataSources = [], isEdit = false) {
  const nameValidation = validateDataSourceName(formData.source_rename, $t, existingDataSources, isEdit)
  const descriptionValidation = validateDataSourceDescription(formData.description, $t)
  
  // Port validation only for TCP/UDP protocols
  let portValidation = { isValid: true, error: '' }
  if (formData.protocol === 'tcp' || formData.protocol === 'udp') {
    portValidation = validatePort(formData.port, $t)
  }
  
  // IP validation only for non-HTTP protocols
  let ipValidation = { isValid: true, error: '' }
  if (formData.protocol !== 'http') {
    ipValidation = validateAllowedIPs(formData.access_ips, $t)
  }

  const isValid = nameValidation.isValid && 
                  descriptionValidation.isValid && 
                  portValidation.isValid && 
                  ipValidation.isValid

  return {
    isValid,
    errors: {
      source_rename: nameValidation.error,
      description: descriptionValidation.error,
      port: portValidation.error,
      access_ips: ipValidation.error
    },
    characterCount: descriptionValidation.characterCount,
    maxDescriptionLength: descriptionValidation.maxLength
  }
}
